import QRCode from 'qrcode';
import { isAr, byId, saveBlob, copyText } from './extra-common.js';
import { trackEvent } from '../core/analytics.js';
import {
  ALL_COUNTRIES,
  detectUserCountry,
  populateCountrySelect,
  sanitizePhoneNumber,
  setupPhoneInputSync,
  attachModernPhoneInput
} from './countries-data.js';

// Official Crisp Vector WhatsApp Icon for Center Badge
const WA_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="128" height="128"><circle cx="24" cy="24" r="23" fill="#25D366"/><path fill="#ffffff" d="M35.1 12.9C32.2 10 28.3 8.4 24.1 8.4 15.6 8.4 8.6 15.4 8.6 23.9c0 2.7.7 5.4 2.1 7.7L8 40l8.6-2.6c2.3 1.2 4.8 1.9 7.5 1.9 8.5 0 15.5-7 15.5-15.5 0-4.1-1.6-8-4.5-10.9zM24.1 36.5c-2.3 0-4.6-.6-6.6-1.8l-.5-.3-4.9 1.5 1.5-4.8-.3-.5c-1.3-2.1-2-4.5-2-7 0-7 5.7-12.7 12.7-12.7 3.4 0 6.6 1.3 9 3.7 2.4 2.4 3.7 5.6 3.7 9 0 7-5.7 12.7-12.6 12.7zm6.9-9.5c-.4-.2-2.2-1.1-2.6-1.2-.3-.1-.6-.2-.8.2-.2.4-.9 1.2-1.1 1.4-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3.1-1.9-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8.2-.2.4-.4.5-.6.2-.2.2-.4.3-.6.1-.2 0-.5-.1-.7-.1-.2-.8-2-1.2-2.7-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.7.1-1 .5-.4.4-1.4 1.4-1.4 3.3s1.4 3.8 1.6 4.1c.2.3 2.8 4.3 6.8 6 1 .4 1.7.7 2.3.9 1 .3 1.9.3 2.6.2.8-.1 2.4-1 2.8-1.9.3-.9.3-1.7.2-1.9-.1-.2-.4-.3-.8-.5z"/></svg>';
const defaultWaImage = new Image();
defaultWaImage.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(WA_SVG)}`;

// DOM Elements
const dialCodeInputEl = byId('wa-dial-code');
const phoneInputEl = byId('wa-phone');
const messageInputEl = byId('wa-message');
const charCountEl = byId('wa-char-count');
const resultLinkInputEl = byId('wa-result-link');
const qrCanvasEl = byId('wa-qrcode-canvas');
const openBtnEl = byId('wa-open-btn');
const copyBtnEl = byId('wa-copy-btn');
const shareBtnEl = byId('wa-share-btn');
const downloadQrBtnEl = byId('wa-download-qr-btn');
const statusTextEl = byId('wa-status-text');
const previewRecipientEl = byId('wa-preview-recipient');
const previewMessageTextEl = byId('wa-preview-message-text');
const previewTimeEl = byId('wa-preview-time');

// QR Logo Controls
const customLogoControlsEl = byId('wa-custom-logo-controls');
const logoFileInputEl = byId('wa-logo-file');
const logoFilenameEl = byId('wa-logo-filename');
const logoClearBtnEl = byId('wa-logo-clear-btn');
const logoChoiceBtns = Array.from(document.querySelectorAll('.wa-logo-choice'));

// State
let currentLogoMode = 'whatsapp'; // 'whatsapp' | 'custom' | 'none'
let customLogoImage = null;
let currentGeneratedUrl = '';
// 1. Draw QR Canvas with WhatsApp or Custom Center Logo
async function renderQrCode(url) {
  if (!qrCanvasEl) return;

  const ctx = qrCanvasEl.getContext('2d');
  if (!url || !url.startsWith('https://wa.me/')) {
    ctx.clearRect(0, 0, qrCanvasEl.width, qrCanvasEl.height);
    return;
  }

  try {
    // Generate high-density QR code with Error Correction Level 'H' (30% redundancy)
    await QRCode.toCanvas(qrCanvasEl, url, {
      width: 512,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: {
        dark: '#075e54', // Official WhatsApp emerald tone
        light: '#ffffff'
      }
    });

    // Clear inline dimensions set by QRCode library so responsive CSS rules take full control
    qrCanvasEl.style.width = '';
    qrCanvasEl.style.height = '';

    // Check if we need to draw a center logo
    let logoToDraw = null;
    if (currentLogoMode === 'whatsapp') {
      logoToDraw = defaultWaImage;
    } else if (currentLogoMode === 'custom' && customLogoImage) {
      logoToDraw = customLogoImage;
    }

    if (logoToDraw) {
      const size = qrCanvasEl.width;
      const tileSize = size * 0.24;
      const pos = (size - tileSize) / 2;

      // Draw clean white protective halo badge
      ctx.fillStyle = '#ffffff';
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(pos, pos, tileSize, tileSize, tileSize * 0.22);
        ctx.fill();
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 3;
        ctx.stroke();
      } else {
        ctx.fillRect(pos, pos, tileSize, tileSize);
      }

      // Draw the logo centered inside the badge
      const logoSize = tileSize * 0.82;
      const logoPos = (size - logoSize) / 2;
      ctx.drawImage(logoToDraw, logoPos, logoPos, logoSize, logoSize);
    }
  } catch (err) {
    console.warn('QR Code generation failed', err);
  }
}

// 6. Main Reactive Update Function
async function updateWhatsAppLink() {
  const dial = dialCodeInputEl?.value || '';
  const rawPhone = phoneInputEl?.value || '';
  const message = messageInputEl?.value || '';

  // Update char counter
  if (charCountEl) {
    charCountEl.textContent = `${message.length} ${isAr ? 'حرف' : 'chars'}`;
  }

  const sanitized = sanitizePhoneNumber(dial, rawPhone);
  const isValid = sanitized.length >= 7 && rawPhone.trim().length > 0;

  if (!isValid) {
    currentGeneratedUrl = '';
    if (resultLinkInputEl) {
      resultLinkInputEl.value = '';
      resultLinkInputEl.placeholder = isAr ? 'أدخل رقم هاتف صالح لتوليد الرابط...' : 'Enter a valid phone number...';
    }
    if (openBtnEl) {
      openBtnEl.setAttribute('aria-disabled', 'true');
      openBtnEl.removeAttribute('href');
    }
    if (statusTextEl) {
      statusTextEl.textContent = isAr ? 'بانتظار إدخال الرقم' : 'Awaiting valid phone';
    }
    if (previewRecipientEl) {
      previewRecipientEl.textContent = `+${dial || '...'} ${rawPhone || '...'}`;
    }
    if (previewMessageTextEl) {
      previewMessageTextEl.textContent = message.trim() || (isAr ? 'مرحباً، أود الاستفسار بخصوص خدماتكم.' : 'Hello, I would like to inquire about your services.');
    }
    renderQrCode('');
    return;
  }

  // Construct official WhatsApp direct wa.me link
  let finalUrl = `https://wa.me/${sanitized}`;
  if (message.trim()) {
    finalUrl += `?text=${encodeURIComponent(message.trim())}`;
  }
  currentGeneratedUrl = finalUrl;

  // Update Result Input
  if (resultLinkInputEl) {
    resultLinkInputEl.value = finalUrl;
  }

  // Update Open Button
  if (openBtnEl) {
    openBtnEl.removeAttribute('aria-disabled');
    openBtnEl.href = finalUrl;
  }

  // Update Status
  if (statusTextEl) {
    statusTextEl.textContent = isAr ? 'رابط نشط وجاهز' : 'Active Link Ready';
  }

  // Update Simulated Chat Bubble Preview
  if (previewRecipientEl) {
    previewRecipientEl.textContent = `+${sanitized}`;
  }
  if (previewMessageTextEl) {
    previewMessageTextEl.textContent = message.trim() || (isAr ? 'مرحباً، أود الاستفسار بخصوص خدماتكم.' : 'Hello, I would like to inquire about your services.');
  }
  if (previewTimeEl) {
    previewTimeEl.textContent = new Date().toLocaleTimeString(isAr ? 'ar-EG' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // Render QR Code
  await renderQrCode(finalUrl);
}

// 7. Event Listeners for Inputs & Actions
function attachInputListeners() {
  [phoneInputEl, messageInputEl].forEach((el) => {
    if (el) {
      el.addEventListener('input', updateWhatsAppLink);
      el.addEventListener('change', updateWhatsAppLink);
    }
  });

  // Template Chips
  document.querySelectorAll('.wa-template-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      if (messageInputEl) {
        messageInputEl.value = chip.dataset.msg || chip.textContent.trim();
        updateWhatsAppLink();
        messageInputEl.focus();
      }
    });
  });

  // Copy Link Button
  copyBtnEl?.addEventListener('click', async () => {
    if (!currentGeneratedUrl) return;
    try {
      await copyText(currentGeneratedUrl);
      copyBtnEl.classList.add('copied');
      const originalText = copyBtnEl.innerHTML;
      copyBtnEl.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span>${isAr ? 'تم النسخ!' : 'Copied!'}</span>
      `;
      trackEvent('tool_interacted', 'whatsapp-link-generator', { action: 'copy_link' });
      setTimeout(() => {
        copyBtnEl.classList.remove('copied');
        copyBtnEl.innerHTML = originalText;
      }, 2000);
    } catch (err) {
      console.warn('Copy link failed', err);
    }
  });

  // Native Web Share API Button
  shareBtnEl?.addEventListener('click', async () => {
    if (!currentGeneratedUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: isAr ? 'رابط محادثة واتساب مباشر' : 'Direct WhatsApp Chat Link',
          url: currentGeneratedUrl
        });
        trackEvent('tool_interacted', 'whatsapp-link-generator', { action: 'web_share' });
      } catch {
        // User cancelled share
      }
    } else {
      // Fallback: trigger copy button
      copyBtnEl?.click();
    }
  });

  // Open Chat Button Analytics
  openBtnEl?.addEventListener('click', () => {
    trackEvent('tool_interacted', 'whatsapp-link-generator', { action: 'open_chat' });
  });

  // Download QR Code as High-Res PNG
  downloadQrBtnEl?.addEventListener('click', () => {
    if (!qrCanvasEl || !currentGeneratedUrl) return;
    qrCanvasEl.toBlob((blob) => {
      if (blob) {
        saveBlob(blob, 'whatsapp-direct-qr.png');
        trackEvent('tool_completed', 'whatsapp-link-generator', { action: 'download_qr' });
      }
    }, 'image/png');
  });

  // QR Logo Mode Toggle
  logoChoiceBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      logoChoiceBtns.forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-checked', 'true');

      currentLogoMode = btn.dataset.logo || 'whatsapp';

      if (customLogoControlsEl) {
        customLogoControlsEl.hidden = currentLogoMode !== 'custom';
      }

      updateWhatsAppLink();
    });
  });

  // Custom Logo File Upload
  logoFileInputEl?.addEventListener('change', () => {
    const file = logoFileInputEl.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(isAr ? 'يرجى اختيار ملف صورة صالح (PNG أو JPG أو SVG أو WebP)' : 'Please choose a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        customLogoImage = img;
        if (logoFilenameEl) logoFilenameEl.textContent = file.name;
        if (logoClearBtnEl) logoClearBtnEl.hidden = false;
        updateWhatsAppLink();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  // Clear Custom Logo
  logoClearBtnEl?.addEventListener('click', () => {
    customLogoImage = null;
    if (logoFileInputEl) logoFileInputEl.value = '';
    if (logoFilenameEl) logoFilenameEl.textContent = '';
    if (logoClearBtnEl) logoClearBtnEl.hidden = true;
    updateWhatsAppLink();
  });
}

// 8. Bootstrap Tool
function init() {
  attachModernPhoneInput({
    fieldEl: byId('wa-phone-field'),
    countryBtnEl: byId('wa-country-btn'),
    flagEl: byId('wa-current-flag'),
    dialBadgeEl: byId('wa-dial-code-badge'),
    dropdownEl: byId('wa-country-dropdown'),
    searchInputEl: byId('wa-country-search'),
    listEl: byId('wa-country-list'),
    phoneInputEl: phoneInputEl,
    dialInputEl: dialCodeInputEl,
    isAr,
    onUpdate: updateWhatsAppLink
  });
  attachInputListeners();

  // Populate sample values for initial visual demonstration
  if (phoneInputEl && !phoneInputEl.value) {
    phoneInputEl.value = '1211555214';
  }
  if (messageInputEl && !messageInputEl.value) {
    messageInputEl.value = isAr ? 'السلام عليكم، أود الاستفسار بخصوص خدماتكم.' : 'Hello! I would like to inquire about your services.';
  }

  updateWhatsAppLink();
  trackEvent('tool_started', 'whatsapp-link-generator');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
